import type { IRunPlan } from '../types';
import { HeadWriter } from '../writers/head';

/**
 * support functions for a Remote View
 */
export abstract class RemoteView {
  constructor(public runPlan: IRunPlan) {}

  /**
   * write the head for the iframe region
   *
   * @abstract
   * @param runPlan the run plan for this frame
   * @returns head code
   */
  async writeHead(): Promise<string> {
    throw 'no-impl';
  }

  /**
   * write the body for the iframe region
   *
   * @abstract
   * @param runPlan the run plan for this frame
   * @returns body code
   */
  async writeBody(): Promise<string> {
    throw 'no-impl';
  }

  /**
   * write the index page for the iframe
   *
   * @abstract
   * @param runPlan the run plan for this frame
   * @param head the head code generated with RemoteView#writeHead
   * @param body the body code generated with RemoteView#writeBody
   * @returns index code
   */
  async writeIndex(runPlan: IRunPlan, head: string, body: string): Promise<string> {
    throw 'no-impl';
  }

  /**
   * @abstract
   * @param runPlan the run plan for this frame
   * @param computationalContext the element to add a script tag to execute the startup sequence on
   * @return void
   */
  async startupRemoteView<E extends HTMLElement>(computationalContext: E) {
    this._writeScriptToContext(this._computeStartupRemoteView(computationalContext), computationalContext);
  }

  /**
   * @return a startup script to start the remote view
   */
  protected _computeStartupRemoteView<E extends HTMLElement>(computationalContext: E) {
    return '';
  }

  /**
   * read the startup remote view script and invoke within the new context
   */
  protected _writeScriptToContext<E extends HTMLElement>(code: string, computationalContext: E) {
    const scriptEl = document.createElement('script');
    const blob = new Blob([code], { type: 'text/javascript' });
    const blobURL = URL.createObjectURL(blob);

    scriptEl.type = 'text/javascript';
    scriptEl.src = blobURL;

    computationalContext.append(scriptEl);
  }

  /**
   * used by a crate and micro
   */
  protected _writeStandardHeader(runPlan: IRunPlan) {
    return [
      HeadWriter.writeTitleCode(runPlan),
      HeadWriter.writeMetaTagsCode(runPlan),
      HeadWriter.writeStyleBlock({ a11y_buddy: true, fouc: true }),
      HeadWriter.writeFrameworkScript(runPlan),
      HeadWriter.writeFrameworkFlags(runPlan)
    ];
  }
}
